import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Rss, Trash2, RefreshCw, ExternalLink, Sparkles, Send, CheckCircle, Pencil, X } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';

const STATUS_STYLES = {
  draft:     'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  scheduled: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  published: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  archived:  'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
};

const errDetail = (err) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
  return err.message || 'Request failed';
};

export default function MicroblogPage() {
  const { selectedProjectId } = useProject();
  const [platforms, setPlatforms] = useState([]);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('posts');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState('');

  // Filter
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Create form
  const [platformId, setPlatformId] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [postUrl, setPostUrl] = useState('');
  const [postStatus, setPostStatus] = useState('draft');

  // Edit mode
  const [editingPost, setEditingPost] = useState(null);

  // AI generate
  const [aiTopic, setAiTopic] = useState('');
  const [aiPlatformId, setAiPlatformId] = useState('');
  const [aiKeywords, setAiKeywords] = useState('');

  const load = async () => {
    try {
      const [platRes, postsRes, statsRes] = await Promise.all([
        api.get('/microblog/platforms'),
        api.get('/microblog/posts'),
        api.get('/microblog/stats'),
      ]);
      setPlatforms(platRes.data.platforms || []);
      setPosts(postsRes.data.posts || []);
      setStats(statsRes.data);
    } catch {
      // silent
    }
  };

  useEffect(() => { load(); }, []);

  const filteredPosts = posts.filter((p) => {
    if (filterPlatform && p.platform_id !== filterPlatform) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    return true;
  });

  const handleEdit = (post) => {
    setEditingPost(post);
    setPlatformId(post.platform_id);
    setTitle(post.title || '');
    setContent(post.content || '');
    setHashtags((post.hashtags || []).map((h) => `#${h}`).join(' '));
    setWebsiteUrl(post.website_url || '');
    setPostUrl(post.post_url || '');
    setPostStatus(post.status || 'draft');
    setActiveTab('create');
    setStatus('');
  };

  const cancelEdit = () => {
    setEditingPost(null);
    setPlatformId(''); setTitle(''); setContent(''); setHashtags('');
    setWebsiteUrl(''); setPostUrl(''); setPostStatus('draft');
    setStatus('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!platformId || !content) { setStatus('Error: Platform and content are required.'); return; }
    setLoading(true);
    setStatus(editingPost ? 'Updating post...' : 'Creating post...');
    try {
      const payload = {
        platform_id: platformId,
        title: title || null,
        content,
        hashtags: hashtags.split(/[\s,]+/).map((h) => h.replace(/^#/, '')).filter(Boolean),
        website_url: websiteUrl || null,
        post_url: postUrl || null,
        status: postStatus,
        project_id: selectedProjectId || undefined,
      };
      if (editingPost) {
        await api.patch(`/microblog/posts/${editingPost.id}`, payload);
        setStatus('Post updated!');
      } else {
        await api.post('/microblog/posts', payload);
        setStatus('Post created!');
      }
      setEditingPost(null);
      setPlatformId(''); setTitle(''); setContent(''); setHashtags('');
      setWebsiteUrl(''); setPostUrl(''); setPostStatus('draft');
      await load();
      setActiveTab('posts');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPublished = async (id) => {
    try {
      const now = new Date().toISOString();
      await api.patch(`/microblog/posts/${id}`, { status: 'published', published_at: now });
      await load();
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/microblog/posts/${id}`);
      await load();
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!aiTopic || !aiPlatformId) { setStatus('Error: Topic and platform are required.'); return; }
    setGenerating(true);
    setStatus('Generating content...');
    try {
      const { data } = await api.post('/microblog/generate', {
        topic: aiTopic,
        platform_id: aiPlatformId,
        keywords: aiKeywords.split(',').map((k) => k.trim()).filter(Boolean),
      });
      setContent(data.content);
      setHashtags(data.hashtags.join(' '));
      setPlatformId(aiPlatformId);
      setStatus('Content generated! Switch to Create tab to edit and save.');
      setActiveTab('create');
    } catch (err) {
      setStatus(`Error: ${errDetail(err)}`);
    } finally {
      setGenerating(false);
    }
  };

  const tabs = [
    { id: 'posts', label: `Posts (${posts.length})` },
    { id: 'create', label: 'Create Post' },
    { id: 'generate', label: 'AI Generate' },
    { id: 'platforms', label: 'Platforms' },
  ];

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
          <Rss className="w-5 h-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Micro Blogging</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Create and track short posts on high-DA micro-blogging platforms for off-page SEO.
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total', value: stats.total },
            { label: 'Published', value: stats.published },
            { label: 'Draft', value: stats.draft },
            { label: 'Scheduled', value: stats.scheduled },
            { label: 'Archived', value: stats.archived },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 shadow-sm text-center">
              <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pb-2 px-1 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === t.id
                ? 'border-b-2 border-violet-600 text-violet-600'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">

        {/* POSTS TAB */}
        {activeTab === 'posts' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex gap-2">
                <select
                  value={filterPlatform}
                  onChange={(e) => setFilterPlatform(e.target.value)}
                  className="p-1.5 text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                >
                  <option value="">All platforms</option>
                  {platforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="p-1.5 text-xs rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                >
                  <option value="">All statuses</option>
                  {['draft', 'scheduled', 'published', 'archived'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <button type="button" onClick={load} className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>

            {filteredPosts.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Rss className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No posts yet. Create or AI-generate your first micro-blog post.</p>
              </div>
            ) : (
              filteredPosts.map((p) => (
                <div key={p.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1 flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white">{p.platform_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[p.status] || STATUS_STYLES.draft}`}>
                          {p.status}
                        </span>
                      </div>
                      {p.title && <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{p.title}</p>}
                      <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">{p.content}</p>
                      {p.hashtags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {p.hashtags.slice(0, 5).map((h) => (
                            <span key={h} className="text-xs text-blue-500 dark:text-blue-400">#{h}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {p.status === 'draft' && (
                        <button type="button" onClick={() => handleEdit(p)} title="Edit draft"
                          className="text-slate-400 hover:text-violet-600 transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {p.status === 'draft' && (
                        <button type="button" onClick={() => handleMarkPublished(p.id)} title="Mark Published"
                          className="text-emerald-500 hover:text-emerald-700 transition-colors">
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {p.post_url && (
                        <a href={p.post_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-violet-600">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button type="button" onClick={() => handleDelete(p.id)} className="text-slate-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* CREATE TAB */}
        {activeTab === 'create' && (
          <form onSubmit={handleCreate} className="space-y-5 max-w-2xl">
            {editingPost && (
              <div className="flex items-center justify-between p-3 rounded-md bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800">
                <p className="text-sm text-violet-700 dark:text-violet-300 font-medium">
                  Editing draft — make your changes and save.
                </p>
                <button type="button" onClick={cancelEdit}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-500 transition-colors">
                  <X className="w-3 h-3" /> Cancel
                </button>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Platform <span className="text-red-500">*</span>
              </label>
              <select
                value={platformId}
                onChange={(e) => setPlatformId(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required
              >
                <option value="">Select platform</option>
                {platforms.map((p) => <option key={p.id} value={p.id}>{p.name} (DA {p.da})</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title (optional)</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Post title..."
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Content <span className="text-red-500">*</span>
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your micro-blog post here..."
                rows={5}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-none"
                required
              />
              <p className="text-xs text-slate-400 mt-1">{content.length} characters</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Hashtags</label>
                <input
                  type="text"
                  value={hashtags}
                  onChange={(e) => setHashtags(e.target.value)}
                  placeholder="#seo #localseo #digitalmarketing"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Status</label>
                <select
                  value={postStatus}
                  onChange={(e) => setPostStatus(e.target.value)}
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                >
                  {['draft', 'scheduled', 'published', 'archived'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Website URL (backlink)</label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yoursite.com"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Published Post URL</label>
                <input
                  type="url"
                  value={postUrl}
                  onChange={(e) => setPostUrl(e.target.value)}
                  placeholder="https://tumblr.com/..."
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-violet-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {loading ? (editingPost ? 'Updating...' : 'Creating...') : (editingPost ? 'Update Post' : 'Create Post')}
            </button>
          </form>
        )}

        {/* AI GENERATE TAB */}
        {activeTab === 'generate' && (
          <form onSubmit={handleGenerate} className="space-y-5 max-w-2xl">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-violet-600" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                AI-generate content for a platform and topic. The result will be pre-filled in the Create tab.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Platform <span className="text-red-500">*</span>
              </label>
              <select
                value={aiPlatformId}
                onChange={(e) => setAiPlatformId(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required
              >
                <option value="">Select platform</option>
                {platforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Topic <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                placeholder="Local SEO tips for restaurants"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Keywords <span className="text-xs text-slate-400">(comma separated)</span>
              </label>
              <input
                type="text"
                value={aiKeywords}
                onChange={(e) => setAiKeywords(e.target.value)}
                placeholder="local seo, restaurant marketing, google maps"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              />
            </div>

            <button
              type="submit"
              disabled={generating}
              className="flex items-center gap-2 bg-violet-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4" />
              {generating ? 'Generating...' : 'Generate Content'}
            </button>
          </form>
        )}

        {/* PLATFORMS TAB */}
        {activeTab === 'platforms' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500 dark:text-slate-400">Supported micro-blogging and content platforms for off-page SEO.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {platforms.map((p) => (
                <div key={p.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4 flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white text-sm">{p.name}</p>
                    <div className="flex gap-3 mt-1 text-xs text-slate-500">
                      <span>DA: <strong className="text-slate-700 dark:text-slate-300">{p.da}</strong></span>
                      <span className="capitalize">{p.type}</span>
                      {p.max_chars > 0 && <span>{p.max_chars.toLocaleString()} char limit</span>}
                    </div>
                  </div>
                  <a href={p.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium shrink-0">
                    Visit <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {status && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            status.startsWith('Error')
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'
          }`}>
            {status}
          </div>
        )}
      </div>
    </motion.div>
  );
}
